import { inject, NgModule } from '@angular/core';
import { CanActivateFn, ExtraOptions, NoPreloading, Routes, RouterModule, Router } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';

import { SearchMainComponent } from './core/search-main/search-main.component';
import { PageNotFoundComponent } from './misc/page-routing/page-not-found/page-not-found.component';
import { LinkListComponent } from './user/link-list/link-list.component';
import { SelectivePreloadingStrategyService } from './selective-preloading-strategy.service';
import { LayoutMainComponent } from './layout-main/layout-main.component';
import { LayoutSimpleComponent } from './layout-simple/layout-simple.component';
import { RedirectComponent } from './redirect/redirect.component';
import type { LeaseDealSummaryComponent } from './lease/lease-deal-summary/lease-deal-summary.component';
import { quarterlyMetricsGuard } from './quarterly-metrics/quarterly-metrics.guard';

/*
  You have two main routes, one for the main layout and one for the stand alone pages.
  If you want any new routes to open in a stand along fashion,
  add them to the 'nm' route. (no menu)

  If the route you are adding is available for both main and standalone pages,
  ADD IT HERE.
*/

// '/leases' is a shortcut into the Properties module that lands on the same listing
// as '/properties' but pre-selects the Leases tab. It redirects (preserving any
// query params, e.g. '?q=barnes') rather than mounting its own copy of the module.
// Note: redirect to '/properties' (not '/properties/listing') - 'listing' is a dead
// child route inside PropertyRoutingModule that re-renders PropertyHomeComponent
// nested inside itself.
const leasesRedirectGuard: CanActivateFn = route => {
  const router = inject(Router);
  return router.createUrlTree(['/properties'], { queryParams: { ...route.queryParams, tab: 'lease' } });
};

const commonRoutes: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./dashboard/dashboard.component').then(m => m.DashboardComponent)
  },
  { path: 'links', component: LinkListComponent },
  { path: 'search', component: SearchMainComponent },
  {
    path: 'start', loadComponent: () =>
      import('./misc/start/start.component').then(m => m.StartComponent)
  },
  {
    path: 'leaseDeals',
    loadComponent: () =>
      import('./lease/lease-deals/lease-deals.component').then(m => m.LeaseDealsComponent)
  },
  {
    path: 'leaseDeals/:id',
    loadComponent: () =>
      import('./lease/lease-deals/lease-deals.component').then(m => m.LeaseDealsComponent)
  },
  {
    path: 'leaseDeals/:id/summary',
    canDeactivate: [(component: LeaseDealSummaryComponent) => component.canLeaveStickyNotes()],
    loadComponent: () =>
      import('./lease/lease-deal-summary/lease-deal-summary.component').then(m => m.LeaseDealSummaryComponent)
  },
  {
    path: 'tenantCompanies/:id/summary',
    loadComponent: () =>
      import('./tenant-company/tenant-company-summary/tenant-company-summary.component').then(m => m.TenantCompanySummaryComponent)
  },
  {
    path: 'appraisals',
    loadChildren: () =>
      import('./appraisal/appraisal.module').then(m => m.AppraisalModule)
  },
  {
    path: 'closingBooks',
    loadChildren: () =>
      import('./closing-book/closing-book.module').then(
        m => m.ClosingBookModule
      )
  },
  {
    path: 'leaseBarneys',
    loadChildren: () =>
      import('./lease-barney/lease-barney.module').then(
        m => m.LeaseBarneyModule
      )
  },
  {
    path: 'tenantCompanies',
    loadChildren: () =>
      import('./tenant-company/tenant-company.module').then(
        m => m.TenantCompanyModule
      )
  },
  {
    path: 'hirrAnalysis',
    loadChildren: () =>
      import('./hirr-analysis/hirr-analysis.module').then(
        m => m.HirrAnalysisModule
      )
  },
  {
    path: 'comps',
    loadChildren: () =>
      import('./comp/comp.module').then(
        m => m.CompModule
      )
  },
  {
    path: 'compsdatabase',
    loadChildren: () =>
      import('./comp/comp.module').then(
        m => m.CompModule
      )
  },
  {
    path: 'unitassignment',
    loadChildren: () =>
      import('./comp/comp.module').then(
        m => m.CompModule
      )
  },

  {
    path: 'esgPipeline',
    loadChildren: () =>
      import('./esg-pipeline/esg-pipeline.module').then(
        m => m.EsgPipelineModule
      )
  },
  {
    path: 'corpEntities',
    loadChildren: () =>
      import('./corp-entity/corp-entity.module').then(
        m => m.CorpEntityModule
      )
  },
  {
    path: 'constructionEfficiencyProjects',
    loadChildren: () =>
      import('./construction-efficiency-project/construction-efficiency-project.module').then(
        m => m.ConstructionEfficiencyProjectModule
      )
  },
  {
    path: 'gresbEfficiencyMeasures',
    loadChildren: () =>
      import('./gresb-efficiency-measures/gresb-efficiency-measures.module').then(
        m => m.GresbEfficiencyMeasuresModule
      )
  },
  {
    path: 'envApprovals',
    loadChildren: () =>
      import('./env-approval/env-approval.module').then(
        m => m.EnvApprovalModule
      )
  },
  {
    path: 'esgCertifications',
    loadChildren: () =>
      import('./esg-certifications/esg-certifications.module').then(
        m => m.EsgCertificationModule
      )
  },
  {
    path: 'budgetLeasing',
    loadChildren: () =>
      import('./budget-leasing/budget-leasing.module').then(
        m => m.BudgetLeasingModule
      )
  },
  {
    path: 'assets',
    redirectTo: 'properties'
  },
  {
    path: 'properties',
    loadChildren: () =>
      import('./property/property.module').then(m => m.PropertyModule),
    data: { preload: true }
  },
  {
    path: 'leases',
    pathMatch: 'full',
    canActivate: [leasesRedirectGuard],
    children: []
  },
  {
    path: 'reports',
    loadChildren: () =>
      import('./report/report.module').then(m => m.ReportModule)
  },
  {
    path: 'investments',
    loadChildren: () =>
      import('./investment/investment.module').then(m => m.InvestmentModule)
  },
  {
    path: 'list',
    loadChildren: () =>
      import('./misc/breaking-news/breaking-news.module').then(
        m => m.BreakingNewsModule
      )
  },
  {
    path: 'breakingNews',
    loadChildren: () =>
      import('./misc/breaking-news/breaking-news.module').then(
        m => m.BreakingNewsModule
      )
  },
  {
    path: 'marchmadness',
    loadChildren: () =>
      import('./misc/march-madness/march-madness.module').then(
        m => m.MarchMadnessModule
      )
  },
  {
    path: 'marketingEvents',
    loadChildren: () =>
      import('./misc/marketing-event/marketing-event.module').then(
        m => m.MarketingEventModule
      )
  },
  {
    path: 'minutes',
    loadChildren: () =>
      import('./misc/minute/minute.module').then(m => m.MinuteModule)
  },
  {
    path: 'vendorCerts',
    loadChildren: () =>
      import('./misc/nnsa-vendor-cert/nnsa-vendor-cert.module').then(
        m => m.NnsaVendorCertModule
      )
  },
  {
    path: 'announcements',
    loadChildren: () =>
      import(
        './misc/transaction-announcement/transaction-announcement.module'
      ).then(m => m.TransactionAnnouncementModule)
  },
  {
    path: 'vehicles',
    loadChildren: () =>
      import('./misc/vehicle/vehicle.module').then(m => m.VehicleModule)
  },
  {
    path: 'vendors',
    loadChildren: () =>
      import('./misc/vendor/vendor.module').then(m => m.VendorModule)
  },
  {
    path: 'workflows',
    loadChildren: () =>
      import('./misc/workflow/workflow.module').then(m => m.WorkflowModule)
  },
  {
    path: 'gresb',
    loadChildren: () =>
      import('./misc/gresb/gresb.module').then(m => m.GresbModule)
  },
  {
    path: 'dataBucket',
    loadChildren: () =>
      import('./misc/data-bucket/data-bucket.module').then(m => m.DataBucketModule)
  },
  {
    path: 'ncreifExport',
    loadChildren: () =>
      import('./misc/ncreif/ncreif.module').then(m => m.NcreifModule)
  },
  {
    path: 'msciExport',
    loadChildren: () =>
      import('./misc/msci/msci.module').then(m => m.MsciModule)
  },
  {
    path: 'users',
    loadChildren: () => import('./user/user.module').then(m => m.UserModule)
  },
  {
    path: 'construction',
    loadChildren: () =>
      import('./construction/construction.module').then(
        m => m.ConstructionModule
      )
  },
  {
    path: 'newconstruction',
    loadChildren: () =>
      import('./construction/construction.module').then(
        m => m.ConstructionModule
      )
  },
  {
    path: 'dispositions',
    loadChildren: () =>
      import('./disposition/disposition.module').then(m => m.DispositionModule)
  },
  {
    path: 'developments',
    loadChildren: () =>
      import('./development/development.module').then(m => m.DevelopmentModule)
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule)
  },
  {
    path: 'contacts',
    loadChildren: () =>
      import('./contact/contact.module').then(m => m.ContactModule)
  },
  {
    path: 'topTenants',
    loadChildren: () =>
      import('./tenants/tenants.module').then(m => m.TenantsModule)
  },
  {
    path: 'gldata',
    loadChildren: () =>
      import('./misc/gldata-transfer/gldata-transfer.module').then(
        m => m.GldataTransferDataModule
      )
  },
  {
    path: 'quarterlyMetrics',
    canActivate: [quarterlyMetricsGuard],
    loadComponent: () =>
      import('./quarterly-metrics/quarterly-metrics.component').then(
        m => m.QuarterlyMetricsComponent
      )
  }
];

const routes: Routes = [
  // main layout with top and left menus
  {
    path: '',
    canActivate: [
      MsalGuard
    ],
    component: LayoutMainComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      ...commonRoutes,
    ]
  },
  // stand alone pages
  {
    path: 'nm',
    canActivate: [
      MsalGuard
    ],
    component: LayoutSimpleComponent,
    children: [
      ...commonRoutes,
    ]
  },
  // redirect bridge for MSAL v5 COOP support - must be outside MsalGuard
  {
    path: 'redirect',
    component: RedirectComponent
  }
];

const isIframe = window !== window.parent && !window.opener;

export function getRoutingOptions(isIframe: boolean, pathname: string): ExtraOptions {
  const isIframeRedirect = isIframe && pathname === '/redirect';
  return {
    paramsInheritanceStrategy: 'emptyOnly',
    // The MSAL bridge must mount in its iframe without preloading authenticated features.
    initialNavigation: !isIframe || isIframeRedirect ? 'enabledBlocking' : 'disabled',
    preloadingStrategy: isIframeRedirect ? NoPreloading : SelectivePreloadingStrategyService
  };
}

@NgModule({
  imports: [RouterModule.forRoot(routes, getRoutingOptions(isIframe, window.location.pathname))],
  exports: [RouterModule],
  providers: [SelectivePreloadingStrategyService]
})
export class AppRoutingModule { }
